import { Button, Checkbox, Flex, Search, Typography } from 'front-commons/ds';
import { useWhenMounted, useResponsive } from 'front-commons/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDrawer from 'stores/drawer';
import usePos from 'stores/pos';
import { Accordions } from '../Accordions';
import { getFilterSearchParams, getValuesFromUrl } from 'containers/FiltersProducts/helpers';
import { useSearch } from '../../hooks/useSearch';
import { HandleSearchParams, SelectedValues } from '../../interfaces';
import { ButtonRemoveFilter, FiltersContainer } from '../../styles';
import type { FiltersProps } from './interfaces';
import { onFilter } from 'shared/gtm/catalog';
import { Icon } from 'hype-ds';

export function Filters({ filters, extrasFilters }: FiltersProps) {
	const [search, setSearch] = useSearchParams();
	const [filterNotFound, setFilterNotFound] = useState(false);
	const [selectedValues, setSelectedValues] = useState<SelectedValues>(() =>
		getValuesFromUrl({ filters, extrasFilters, searchParams: search }),
	);

	const { handleCloseAllDrawers } = useDrawer();
	const { small, medium } = useResponsive();
	const {
		searchValue,
		suggestions,
		clearSearch,
		getFilterOption,
		getFilterOptionName,
		getExtraFilterOptionName,
		handleClearSuggestions,
		handleSearchSuggestions,
		setSearchValue,
	} = useSearch({ filters, extrasFilters });
	const { posStore } = usePos();
	const pos = posStore.selectedPos;

	const productType = search.get('promocoes');
	const categories = search.get('categorias');
	const brands = search.get('marcas');
	const mecanics = search.get('mecanicas');
	const validity = search.get('vigencia');
	const release = search.get('lancamentos');
	const industries = search.get('industrias');

	const hasItemsInFilters =
		productType?.length ||
		categories?.length ||
		brands?.length ||
		mecanics?.length ||
		release?.length ||
		validity?.length ||
		industries?.length;

	const hasExtraFiltersSelected = extrasFilters?.some(({ label, paramName }) =>
		paramName
			? selectedValues.some((section) => section.name === paramName && section.values?.length)
			: search.get(label) === 's',
	);
	const groupedFilterNamesKey = JSON.stringify(
		Array.from(new Set(extrasFilters?.flatMap(({ paramName }) => (paramName ? [paramName] : [])) || [])),
	);

	const allOptions = filters.flatMap((filter) => filter.options.map((option) => ({ ...option, name: filter.name })));
	const groupedExtras = extrasFilters?.filter(({ paramName }) => !!paramName);
	const ungroupedExtras = extrasFilters?.filter(({ paramName }) => !paramName);

	const filteredOptions = selectedValues
		.flatMap((item) => item.values)
		.map((value) => allOptions.find((option) => option.value === value))
		.filter((i) => !!i);

	const mappedFilters = useMemo(() => {
		const mapped = filters
			.filter((filter) => filter.options?.length)
			.map((filter) => {
				if (filter.name === 'promocoes') {
					const promotionOption = filter.options.find((option) => option.label === 'COMBO');
					const shouldRenderProductType = !!promotionOption || filter.options.length > 2;
					const filteredOptions = filter.options.filter((option) => option.label !== 'SIMPLE');

					return {
						...filter,
						options: shouldRenderProductType ? filteredOptions : [],
					};
				}

				if (filter.options.length < 2) {
					return { ...filter, singleOption: true };
				}

				return filter;
			})
			.filter((filter) => {
				return filter.name !== 'promocoes' || !!filter.options.find((option) => option.label === 'COMBO');
			});

		const productTypeIndex = mapped.findIndex((filter) => filter.name === 'promocoes');

		if (productTypeIndex > -1) {
			const productTypeFilter = mapped.splice(productTypeIndex, 1)[0];

			mapped.unshift(productTypeFilter);
		}

		return mapped;
	}, [filters]);

	function handleSearchByValue(value?: string) {
		if (!value) return;

		if (value.length < 3) {
			setFilterNotFound(true);

			return;
		}

		const option = getFilterOption(value);

		if (!option) {
			handleClearSuggestions();
			setFilterNotFound(true);

			return;
		}

		handleSearch({ searchedValue: option.value, isSearch: true });

		onFilter({
			filterType: 'busca',
			filterTerm: value,
		});
	}

	function handleSearch({ searchedValue, isSearch, isExtraFilter }: HandleSearchParams) {
		const name = isExtraFilter ? getExtraFilterOptionName(searchedValue) : getFilterOptionName(searchedValue);

		const option = isExtraFilter ? { label: name, value: 's' } : allOptions.find((o) => o.value === searchedValue);

		if (!name) {
			setFilterNotFound(true);

			return;
		}

		handleClearSearch();

		setSelectedValues((prevState) => {
			const index = prevState.findIndex((item) => item.name === name);

			if (index !== -1) {
				const item = prevState[index];

				if (item.values?.includes(searchedValue)) {
					return prevState;
				}

				const updatedItem = {
					...item,
					values: [...(item.values || []), searchedValue],
				};

				return [...prevState.slice(0, index), updatedItem, ...prevState.slice(index + 1)];
			}

			return [...prevState, { name, values: [searchedValue] }];
		});

		if (!isSearch) {
			onFilter({
				filterType: name,
				filterTerm: (isExtraFilter ? option?.value : option?.label) || searchedValue,
			});
		}
	}

	function handleClearSearch() {
		clearSearch();
		handleClearSuggestions();
		setFilterNotFound(false);
		setSearchValue('');
	}

	const handleFilterChange = useCallback(
		(updatedValues?: SelectedValues, removeFilters?: boolean) => {
			const values = updatedValues || selectedValues;
			const next = getFilterSearchParams(search, values, extrasFilters, removeFilters);

			if (next.toString() !== search.toString()) setSearch(next);
		},
		[selectedValues, extrasFilters, search, setSearch],
	);

	function handleChangeGroupedFilter(name: string, value: string, label: string, checked: boolean) {
		const isSelected = !!selectedValues.find((section) => section.name === name)?.values?.includes(value);
		if (isSelected === checked) return;

		setSelectedValues((previous) => {
			const current = previous.find((section) => section.name === name)?.values || [];
			const values = checked ? [...current, value] : current.filter((item) => item !== value);

			return [...previous.filter((section) => section.name !== name), { name, values }];
		});
		onFilter({ filterType: name, filterTerm: label });
	}

	function handleRemoveFilter(sectionName: string, value: string) {
		const section = selectedValues.find((i) => i.name === sectionName);

		if (section?.values?.length === 1) {
			handleClearFilter(sectionName);

			if (extrasFilters?.some((extraFilter) => extraFilter.value === value)) {
				return;
			}
		}

		setSelectedValues((prev) =>
			prev
				.map((section) =>
					section.name === sectionName
						? {
								...section,
								values: section.values?.filter((v) => v !== value) || [],
						  }
						: section,
				)
				.filter(({ values }) => values && values.length),
		);
	}

	function handleClearFilter(name?: string) {
		if (!name) {
			if (location.pathname.includes('categorias')) {
				setSearch({ categoria: `${search.get('categoria')}` });
				setSelectedValues([{ name: 'categoria', values: [`${search.get('categoria')}`] }]);
				return;
			}

			setSearch({});
			setSelectedValues([]);
			return;
		}

		setSelectedValues((previous) => previous.filter((section) => section.name !== name));

		return setSearch((prevState) => {
			prevState.delete(name);

			return prevState;
		});
	}

	function handleApplyMobileFilters() {
		if (selectedValues.length > 0 || extrasFilters?.some(({ paramName }) => paramName)) {
			handleFilterChange?.(selectedValues, true);
		} else {
			handleClearFilter?.();
		}

		handleCloseAllDrawers();
	}

	useEffect(() => {
		if (!searchValue) setFilterNotFound(false);
	}, [searchValue]);

	useEffect(() => {
		if (medium) handleFilterChange();
	}, [selectedValues, medium]);

	useEffect(() => {
		const groupedNames = extrasFilters?.flatMap(({ paramName }) => (paramName ? [paramName] : [])) || [];
		if (!groupedNames.length) return;

		const restored = getValuesFromUrl({ extrasFilters, searchParams: search });
		setSelectedValues((previous) => {
			const currentGroups = previous.filter(({ name }) => groupedNames.includes(name));
			const restoredGroups = restored.filter(({ name }) => groupedNames.includes(name));

			if (JSON.stringify(currentGroups) === JSON.stringify(restoredGroups)) return previous;

			return [...previous.filter(({ name }) => !groupedNames.includes(name)), ...restoredGroups];
		});
	}, [search, groupedFilterNamesKey]);

	useWhenMounted(() => {
		handleClearFilter();
	}, [pos?.id]);

	const showFilteredOptions = (hasItemsInFilters && !!filteredOptions.length) || (small && !!filteredOptions.length);

	return (
		<FiltersContainer>
			<Search
				placeholder="Busque pelo filtro desejado"
				suggestions={suggestions}
				onChange={handleSearchSuggestions}
				onClear={handleClearSearch}
				onClickSuggestion={(suggestion) => {
					handleSearch({ searchedValue: suggestion.value, isSearch: true });
					onFilter({
						filterType: 'busca',
						filterTerm: suggestion.value,
					});
				}}
				onSearch={handleSearchByValue}
				value={searchValue}
				feedback={
					filterNotFound
						? {
								type: 'error',
								message: 'Filtro não encontrado',
						  }
						: undefined
				}
			/>
			{showFilteredOptions && (
				<Flex direction="column" gap="1rem">
					<Flex gap="0.25rem" direction="column">
						<Typography variant="Paragraph/Semibold">Filtrando por:</Typography>

						<Flex wrap="wrap" gap="8px">
							{filteredOptions.map((option) => {
								return (
									<Flex
										alignItems="center"
										justifyContent="space-between"
										borderRadius="8px"
										backgroundColor="--greys-100"
										padding="0.25rem 0.5rem"
										gap="8px"
									>
										<Typography variant="Paragraph/Semibold">{option.label.capitalize()}</Typography>

										<ButtonRemoveFilter variant="none" onClick={() => handleRemoveFilter(option.name, option.value)}>
											<Icon name="close" size="12px" color="--neutral-white" />
										</ButtonRemoveFilter>
									</Flex>
								);
							})}
						</Flex>
					</Flex>

					<Button data-testid="clear-filter" onClick={() => handleClearFilter()} variant="text">
						Limpar filtros
					</Button>
				</Flex>
			)}

			{!showFilteredOptions && !!hasExtraFiltersSelected && (
				<Flex direction="column" gap="1rem">
					<Button data-testid="clear-filter" onClick={() => handleClearFilter()} variant="text">
						Limpar filtros
					</Button>
				</Flex>
			)}

			{!!groupedExtras?.length && (
				<Flex direction="column" gap="8px">
					<Typography variant="ParagraphSmall/Regular" color="--text-secondary">
						{groupedExtras[0].groupTitle || 'Produtos por'}
					</Typography>
					<Flex direction="column" gap="16px">
						{groupedExtras.map(({ label, value, paramName }) => {
							if (!paramName) return null;

							const isSelected = !!selectedValues
								.find((section) => section.name === paramName)
								?.values?.includes(value);

							return (
								<Checkbox
									key={`${paramName}-${value}-${isSelected}`}
									name={value}
									onChange={(values) => handleChangeGroupedFilter(paramName, value, label, values.includes(value))}
									initialValue={isSelected ? [value] : undefined}
									options={[{ label, value }]}
									autoCapitalize={false}
									typographyProps={{ variant: 'Paragraph/Regular' }}
									size="medium"
								/>
							);
						})}
					</Flex>
				</Flex>
			)}

			{ungroupedExtras?.map(({ label, value }) => {
				const isSelected = search.get(label) === 's';

				return (
					<Button
						key={`${label}-${value}`}
						style={{ borderRadius: 0 }}
						width="100%"
						variant="none"
						onClick={() =>
							!isSelected
								? handleSearch({ searchedValue: value, isExtraFilter: true })
								: handleRemoveFilter(label, value)
						}
					>
						<Checkbox
							key={`${value}-${isSelected}`}
							name={value}
							onChange={() => {}}
							initialValue={isSelected ? [value] : undefined}
							options={[{ label: label.capitalize(), value }]}
							typographyProps={{ variant: 'Paragraph/Regular' }}
							size="medium"
						/>
					</Button>
				);
			})}

			<Accordions filters={mappedFilters} handleSelectedFilters={handleSearch} />

			{small && (
				<Button data-testid="apply-filters-btn" width="100%" onClick={handleApplyMobileFilters}>
					Aplicar filtros
				</Button>
			)}
		</FiltersContainer>
	);
}
